import * as XLSX from "xlsx";

const TEMPLATE_FILENAME = "cpd-location-trainers-template.xlsx";

export function downloadVenueTrainersTemplate() {
  const wb = XLSX.utils.book_new();
  const instructions = XLSX.utils.aoa_to_sheet([
    ["Training location — bulk add trainers (template)"],
    [""],
    [
      "1. Use the Trainers sheet: one trainer per row in the TrainerSearchTerm column (or NIDNumber / Phone / Email column).",
    ],
    [
      "2. Enter the same value as in the app search: national ID, phone number, or email. Format NID as Text in Excel so digits are not shortened.",
    ],
    ["3. Trainers must already be registered under your training provider."],
    [
      "4. Upload the file in the training wizard location panel, then review the list before saving the location.",
    ],
  ]);
  instructions["!cols"] = [{ wch: 92 }];
  XLSX.utils.book_append_sheet(wb, instructions, "Instructions");

  const trainers = XLSX.utils.aoa_to_sheet([["TrainerSearchTerm"], [""]]);
  trainers["!cols"] = [{ wch: 28 }];
  XLSX.utils.book_append_sheet(wb, trainers, "Trainers");

  XLSX.writeFile(wb, TEMPLATE_FILENAME);
}

function normalizeHeaderKey(cellValue) {
  return String(cellValue ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");
}

const COLUMN_PRIORITY = [
  "trainersearchterm",
  "trainersearch",
  "searchterm",
  "nidnumber",
  "nid",
  "nationalid",
  "phone",
  "phonenumber",
  "email",
  "trainer",
  "identifier",
];

function sheetToMatrix(sheet) {
  return XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" }) || [];
}

function cellToString(raw) {
  if (raw === null || raw === undefined) return "";
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return raw >= 1e15 ? String(Math.round(raw)) : String(raw);
  }
  return String(raw).trim();
}

/**
 * Reads an .xlsx from the location-trainer template (or compatible single-column list).
 * @returns {{ terms: string[], error: string }}
 */
export function extractTrainerSearchTermsFromVenueTrainerXlsx(arrayBuffer) {
  const wb = XLSX.read(arrayBuffer, { type: "array" });
  const trainersName = wb.SheetNames.find((n) => n.trim().toLowerCase() === "trainers");
  const sheetName = trainersName || wb.SheetNames[0];
  const sheet = sheetName ? wb.Sheets[sheetName] : null;
  if (!sheet) return { terms: [], error: "No worksheet found." };

  const matrix = sheetToMatrix(sheet);
  if (!matrix.length) return { terms: [], error: "The sheet is empty." };

  const headerRow = matrix[0] || [];
  const colMap = {};
  headerRow.forEach((cell, idx) => {
    const key = normalizeHeaderKey(cell);
    if (key) colMap[key] = idx;
  });

  let valueColIdx = null;
  for (const k of COLUMN_PRIORITY) {
    if (colMap[k] != null) {
      valueColIdx = colMap[k];
      break;
    }
  }

  if (valueColIdx == null && headerRow.length === 1 && String(headerRow[0] ?? "").trim() === "") {
    valueColIdx = 0;
  }
  if (valueColIdx == null && matrix[0]?.length === 1) {
    valueColIdx = 0;
  }

  if (valueColIdx == null) {
    return {
      terms: [],
      error:
        "Could not find a column such as TrainerSearchTerm, NIDNumber, Phone, or Email. Use the downloaded template or add a header row.",
    };
  }

  const terms = [];
  const seen = new Set();
  for (let r = 1; r < matrix.length; r++) {
    const row = matrix[r] || [];
    const term = cellToString(row[valueColIdx]).replace(/\s+/g, " ").trim();
    if (!term) continue;
    const key = term.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    terms.push(term);
  }

  return { terms, error: "" };
}
